import { addDays, format } from "date-fns";
import { sum } from "lodash";

import { store } from "app/store";
import { setDataParams } from "components/word-processor-field/word-processor.slice";
import {
  DOME_SUBTYPE,
  LEFT_EAR_TYPE,
  RECEIVER_SUBTYPE,
  RIGHT_EAR_TYPE
} from "constants/products";
import {
  ACCESSORY_PRODUCT_TYPE,
  FEE_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  SERVICE_PRODUCT_TYPE
} from "constants/sales";
import TITLES from "constants/titles";
import type {
  DomeInventoryProductSpecification,
  ReceiverInventoryProductSpecification
} from "services/types/inventory-products.type";
import type { LineItem, Sale } from "services/types/sales.type";
import { formatMoneyValue, getFullName, roundTo } from "utils/common";

import { PATIENT_SELECTED_INVOICE_OPTIONS } from "../merge-tags-options/patient-selected-invoice.utils";
import {
  buildMergeTagOptionsString,
  getDateOrNull,
  getSaleMergeTagOrPlaceholder
} from "../merge-tags.utils";
import { getCatalogName } from "./common/configurable-merge-tags";
import {
  decodeItemsAsDrawnOptions,
  itemsAsDrawnTableHtml
} from "./common/items-as-drawn.util";
import {
  getSaleDeviceLineItems,
  getSaleLineItemsServicesTableHtml,
  getSaleLineItemsTableHtml,
  getSaleLineItemsTableHtmlWithPredefinedParameters,
  getSaleLineItemsTotalsTableHtml
} from "./common/sale-items-table.utils";
import {
  excludeReturnedOrExchanged,
  getLineItemNetTotal,
  getSaleTotals,
  isReturnedOrExchanged
} from "./common/utils";
import { MergeTagsReplaceHandler } from "./merge-tags-values";
import {
  combineBinauralValues,
  getFirstHearingAidFromSale,
  getHearingAidDeliveryDate,
  getHearingAidLossDamageWarrantyEnd,
  getHearingAidSerialNumber,
  getHearingAidServicePlanExpiration,
  getHearingAidWarrantyEnd,
  getHearingAidWarrantyStart,
  getPatientProductEar
} from "./patient-selected-invoice-hearing-aids.utils";

const findAccessoryItem = (
  items: LineItem[] | undefined,
  subtype: number,
  ear: "L" | "R"
) =>
  items?.find((i) => {
    if (isReturnedOrExchanged(i)) {
      return false;
    }
    if (
      i.product?.master_product?.type !== ACCESSORY_PRODUCT_TYPE ||
      i.product?.master_product?.subtype !== subtype
    ) {
      return false;
    }
    // Check ear in multiple locations, matching the hearing-aid line-item pattern
    const patientProducts = (i as any).patient_products;
    const orderItem = (i as any).order_item;
    const itemEar =
      i.inventory_product?.ear ??
      (Array.isArray(patientProducts)
        ? getPatientProductEar(patientProducts[0])
        : undefined) ??
      orderItem?.patient_product?.ear;
    return itemEar === ear;
  });

/**
 * Resolves a "Both Hearing Aids" (binaural) expiration-date tag: the per-ear getter runs
 * against the first left and first right hearing aid on the invoice and the two results
 * are folded into one string by combineBinauralValues. With no invoice selected it
 * renders the "Click to Select Invoice" placeholder; with an invoice but no dates it
 * renders "". Same contract as HA_DELIVERY_DATE below.
 */
const getBinauralHearingAidValue = (
  sale: Sale | undefined,
  mergeTagName: string,
  getEarValue: (item: LineItem | undefined, earType: string) => string
): string => {
  if (!sale) {
    return getSaleMergeTagOrPlaceholder(null, mergeTagName);
  }

  const leftItem = getFirstHearingAidFromSale(sale, LEFT_EAR_TYPE);
  const rightItem = getFirstHearingAidFromSale(sale, RIGHT_EAR_TYPE);

  return combineBinauralValues(
    getEarValue(leftItem, LEFT_EAR_TYPE),
    getEarValue(rightItem, RIGHT_EAR_TYPE)
  );
};

const isHearingAidLine = (item: LineItem) =>
  item.product?.master_product?.type === HEARING_AID_PRODUCT_TYPE;

const isServiceOrFeeLine = (item: LineItem) =>
  item.product?.master_product?.type === SERVICE_PRODUCT_TYPE ||
  item.product?.master_product?.type === FEE_PRODUCT_TYPE;

// A line at list price: price × quantity, discounts ignored, before tax.
const getLineItemListTotal = (item: LineItem) =>
  (Number(item.price) || 0) * (Number(item.quantity) || 1);

// Sums one amount per matching, non-returned line of the selected invoice as money;
// null (tag left unresolved) when no invoice or no line items are selected.
const sumInvoiceLines = (
  sale: Sale | undefined,
  matches: (item: LineItem) => boolean,
  amountOf: (item: LineItem) => number
): string | null => {
  const items = sale?.items as LineItem[] | undefined;
  if (!items || items.length === 0) return null;

  return formatMoneyValue(
    roundTo(
      sum(excludeReturnedOrExchanged(items).filter(matches).map(amountOf)),
      2
    )
  );
};

// A line to write on: the print dialog makes a run of underscores a field staff type into (SN-19949).
const FILL_IN_LINE = "__________";

export const PATIENT_SELECTED_INVOICE_VALUES: MergeTagsReplaceHandler = {
  [PATIENT_SELECTED_INVOICE_OPTIONS.SELECT_AN_INVOICE]: ({ id }) => {
    store.dispatch(setDataParams({ id, openSelectSaleDialog: true }));
    return null;
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.DEFINE_PARAMETERS]: ({
    id,
    data,
    options
  }) => {
    store.dispatch(
      setDataParams({ id, openSelectSaleConfigurationDialog: true })
    );
    return null;
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.WITH_PREDEFINED_PARAMETERS]: ({
    id,
    data,
    options
  }) => {
    if (!options || !Object.values(options).length) {
      store.dispatch(
        setDataParams({ id, openSelectSaleConfigurationDialog: true })
      );
      return null;
    }

    return getSaleMergeTagOrPlaceholder(
      getSaleLineItemsTableHtmlWithPredefinedParameters(
        data.contextSale,
        options
      ),
      PATIENT_SELECTED_INVOICE_OPTIONS.WITH_PREDEFINED_PARAMETERS,
      buildMergeTagOptionsString(options)
    );
  },
  // ENG-2463: never opens the parameters dialog: its options are the table's layout, written by the converter.
  [PATIENT_SELECTED_INVOICE_OPTIONS.ITEMS_AS_DRAWN]: ({ data, options }) => {
    const layout = decodeItemsAsDrawnOptions(options);
    if (!layout) return "";

    return getSaleMergeTagOrPlaceholder(
      itemsAsDrawnTableHtml(data.contextSale, layout),
      PATIENT_SELECTED_INVOICE_OPTIONS.ITEMS_AS_DRAWN,
      buildMergeTagOptionsString(options)
    );
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.SALE_ITEMS]: ({ data }) => {
    return getSaleMergeTagOrPlaceholder(
      getSaleLineItemsTableHtml(
        data.contextSale,
        getSaleDeviceLineItems,
        true,
        false,
        true
      ),
      PATIENT_SELECTED_INVOICE_OPTIONS.SALE_ITEMS
    );
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.DEVICES]: ({ data }) => {
    return getSaleMergeTagOrPlaceholder(
      getSaleLineItemsTableHtml(data.contextSale, getSaleDeviceLineItems),
      PATIENT_SELECTED_INVOICE_OPTIONS.DEVICES
    );
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.DEVICES_NO_PRICE]: ({ data }) => {
    return getSaleMergeTagOrPlaceholder(
      getSaleLineItemsTableHtml(
        data.contextSale,
        getSaleDeviceLineItems,
        false,
        true
      ),
      PATIENT_SELECTED_INVOICE_OPTIONS.DEVICES_NO_PRICE
    );
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.SERVICES]: ({ data }) => {
    return getSaleMergeTagOrPlaceholder(
      getSaleLineItemsServicesTableHtml(data.contextSale),
      PATIENT_SELECTED_INVOICE_OPTIONS.SERVICES
    );
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.SERVICES_NO_PRICE]: ({ data }) => {
    return getSaleMergeTagOrPlaceholder(
      getSaleLineItemsServicesTableHtml(data.contextSale, true),
      PATIENT_SELECTED_INVOICE_OPTIONS.SERVICES_NO_PRICE
    );
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.TOTALS]: ({ data }) => {
    return getSaleMergeTagOrPlaceholder(
      getSaleLineItemsTotalsTableHtml(data.contextSale),
      PATIENT_SELECTED_INVOICE_OPTIONS.TOTALS
    );
  },
  // The two original totals are list price (price × quantity, before tax): every purchase
  // agreement that already uses them keeps printing what it always has. The "AfterDiscounts"
  // pair is the same sum net of per-unit discounts, the price the patient pays (ENG-2432,
  // split into its own tags by ENG-2439). The Totals tables are left alone: they print the
  // gross total and a separate "Total Discounts" row.
  [PATIENT_SELECTED_INVOICE_OPTIONS.TOTAL_COST_OF_HEARING_AIDS]: ({ data }) =>
    sumInvoiceLines(data.contextSale, isHearingAidLine, getLineItemListTotal),
  [PATIENT_SELECTED_INVOICE_OPTIONS.TOTAL_COST_OF_SERVICES]: ({ data }) =>
    sumInvoiceLines(data.contextSale, isServiceOrFeeLine, getLineItemListTotal),
  [PATIENT_SELECTED_INVOICE_OPTIONS.TOTAL_COST_OF_HEARING_AIDS_AFTER_DISCOUNTS]:
    ({ data }) =>
      sumInvoiceLines(data.contextSale, isHearingAidLine, getLineItemNetTotal),
  [PATIENT_SELECTED_INVOICE_OPTIONS.TOTAL_COST_OF_SERVICES_AFTER_DISCOUNTS]: ({
    data
  }) =>
    sumInvoiceLines(data.contextSale, isServiceOrFeeLine, getLineItemNetTotal),
  // ENG-2432: the invoice's own Total Amount and Balance Due as single values — the same
  // numbers the Totals table prints in its last two rows (getTotalsHtmlRow).
  [PATIENT_SELECTED_INVOICE_OPTIONS.TOTAL_AMOUNT]: ({ data }) => {
    if (!data.contextSale) return null;

    return formatMoneyValue(getSaleTotals(data.contextSale).total);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.BALANCE_DUE]: ({ data }) => {
    if (!data.contextSale) return null;

    const { balance } = getSaleTotals(data.contextSale);
    return formatMoneyValue(balance, balance < 0);
  },
  // ENG-2463: the card surcharge collected on the sale's patient payments, the figure the Totals table
  // prints as "Total Surcharge". Before any is collected (an agreement signed before payment) it is a line
  // the print dialog lets staff type into: the practice's rate applied to a total would be a number nobody
  // charged.
  [PATIENT_SELECTED_INVOICE_OPTIONS.CREDIT_CARD_FEE]: ({ data }) => {
    if (!data.contextSale) return null;

    const { totalPatientSurcharge } = getSaleTotals(data.contextSale);
    return totalPatientSurcharge > 0
      ? formatMoneyValue(totalPatientSurcharge)
      : FILL_IN_LINE;
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.TRIAL_PERIOD_DURATION_DAYS]: ({
    data,
    extraData
  }) => {
    if (!extraData) return null;

    const value = extraData.trialPeriodDurationDays;

    return value ? `${value} day${Number(value) > 1 ? "s" : ""}` : "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RESTOCKING_RETURN_FEE]: ({ extraData }) => {
    if (!extraData) return null;

    const value = extraData.restockingReturnFee;

    return value ? formatMoneyValue(value) : "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LOSS_DAMAGE_DEDUCTIBLE]: ({
    extraData
  }) => {
    if (!extraData) return null;

    const value = extraData.lossDamageDeductible;

    return value ? formatMoneyValue(value) : "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.WARRANTY_DURATION]: ({ extraData }) => {
    if (!extraData) return null;

    const value = extraData.warrantyDuration;

    return value ? `${value} month${Number(value) > 1 ? "s" : ""}` : "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.MEMO]: ({ data, extraData }) => {
    if (!extraData) return null;

    const value = extraData.memo;
    return value ?? "";
  },
  //
  [PATIENT_SELECTED_INVOICE_OPTIONS.PURCHASE_AGREEMENT_DATE]: ({
    extraData
  }) => {
    if (!extraData) return null;

    const value = getDateOrNull(extraData.purchaseAgreementDate);
    return value ? format(value, "MM/dd/yyyy") : "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.TRIAL_PERIOD_END_DATE]: ({ extraData }) => {
    if (!extraData) return null;

    const value = Number(extraData.trialPeriodDurationDays);
    const purchaseAgreementDate = getDateOrNull(
      extraData.purchaseAgreementDate
    );
    if (isNaN(value) || !purchaseAgreementDate) return "";

    return format(addDays(purchaseAgreementDate, value), "MM/dd/yyyy");
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_FULL_NAME]: ({ data }) => {
    if (!data.contextSale?.provider) return null;

    const provider = data.contextSale.provider;

    return getFullName({
      firstName: provider?.first_name ?? "",
      lastName: provider?.last_name ?? ""
    });
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_FULL_NAME_WITH_TITLE]: ({
    data
  }) => {
    if (!data.contextSale?.provider) return null;

    const provider = data.contextSale.provider;
    return getFullName({
      firstName: provider?.first_name ?? "",
      lastName: provider?.last_name ?? "",
      title: provider?.title
    });
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_FIRST_NAME]: ({ data }) => {
    if (!data.contextSale?.provider) return null;

    const provider = data.contextSale.provider;
    return provider?.first_name ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_LAST_NAME]: ({ data }) => {
    if (!data.contextSale?.provider) return null;

    const provider = data.contextSale.provider;
    return provider?.last_name ?? "";
  },

  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_TITLE]: ({ data }) => {
    if (!data.contextSale?.provider) return null;

    const title = data.contextSale.provider?.title ?? "";
    return title ? TITLES.getFromCode(title) : "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_SUFFIX]: ({ data }) => {
    if (!data.contextSale?.provider) return null;

    const provider = data.contextSale.provider;
    return provider?.suffix ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_LICENSE_NUMBER]: ({ data }) => {
    if (!data.contextSale?.provider) return null;

    const provider = data.contextSale.provider;
    return provider?.resolved_license_number || provider?.license_number || "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_SIGNATURE]: ({ data }) => {
    if (!data.contextSale?.provider) return null;

    const provider = data.contextSale.provider;
    return provider?.signature
      ? `<img src="${provider?.signature}" alt="Provider mergetag signature" width="350" />`
      : null;
  },

  // Left Hearing Aid merge tags
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_MANUFACTURER]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, LEFT_EAR_TYPE);
    return item?.product?.master_product?.manufacturer?.name ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_MODEL]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, LEFT_EAR_TYPE);
    return item?.product?.master_product?.model ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_NAME]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, LEFT_EAR_TYPE);
    return item ? getCatalogName(item) : "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_SERIAL_NUMBER]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, LEFT_EAR_TYPE);
    return getHearingAidSerialNumber(item, LEFT_EAR_TYPE);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_WARRANTY_START]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, LEFT_EAR_TYPE);
    return getHearingAidWarrantyStart(item, LEFT_EAR_TYPE);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_WARRANTY_END]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, LEFT_EAR_TYPE);
    return getHearingAidWarrantyEnd(item, LEFT_EAR_TYPE);
  },

  // Right Hearing Aid merge tags
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_MANUFACTURER]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, RIGHT_EAR_TYPE);
    return item?.product?.master_product?.manufacturer?.name ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_MODEL]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, RIGHT_EAR_TYPE);
    return item?.product?.master_product?.model ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_NAME]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, RIGHT_EAR_TYPE);
    return item ? getCatalogName(item) : "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_SERIAL_NUMBER]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, RIGHT_EAR_TYPE);
    return getHearingAidSerialNumber(item, RIGHT_EAR_TYPE);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_WARRANTY_START]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, RIGHT_EAR_TYPE);
    return getHearingAidWarrantyStart(item, RIGHT_EAR_TYPE);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_WARRANTY_END]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, RIGHT_EAR_TYPE);
    return getHearingAidWarrantyEnd(item, RIGHT_EAR_TYPE);
  },

  // Hearing Aid Delivery Date merge tags
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_DELIVERY_DATE]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, LEFT_EAR_TYPE);
    return getHearingAidDeliveryDate(item, LEFT_EAR_TYPE);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_DELIVERY_DATE]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, RIGHT_EAR_TYPE);
    return getHearingAidDeliveryDate(item, RIGHT_EAR_TYPE);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.HA_DELIVERY_DATE]: ({ data }) => {
    // No invoice selected: show the "Click to Select Invoice" placeholder.
    if (!data.contextSale) {
      return getSaleMergeTagOrPlaceholder(
        null,
        PATIENT_SELECTED_INVOICE_OPTIONS.HA_DELIVERY_DATE
      );
    }

    const leftItem = getFirstHearingAidFromSale(
      data.contextSale,
      LEFT_EAR_TYPE
    );
    const rightItem = getFirstHearingAidFromSale(
      data.contextSale,
      RIGHT_EAR_TYPE
    );
    const left = getHearingAidDeliveryDate(leftItem, LEFT_EAR_TYPE) || null;
    const right = getHearingAidDeliveryDate(rightItem, RIGHT_EAR_TYPE) || null;

    // Invoice selected but no delivery dates: return empty (not the placeholder).
    if (left && right && left === right) return left;
    if (left && right) return `L: ${left} R: ${right}`;
    if (left) return `L: ${left}`;
    if (right) return `R: ${right}`;
    return "";
  },

  // Both Hearing Aids expiration dates — same binaural resolution as HA_DELIVERY_DATE.
  [PATIENT_SELECTED_INVOICE_OPTIONS.HA_MANUFACTURER_WARRANTY_EXPIRATION_DATE]:
    ({ data }) =>
      getBinauralHearingAidValue(
        data.contextSale,
        PATIENT_SELECTED_INVOICE_OPTIONS.HA_MANUFACTURER_WARRANTY_EXPIRATION_DATE,
        getHearingAidWarrantyEnd
      ),
  [PATIENT_SELECTED_INVOICE_OPTIONS.HA_LOSS_DAMAGE_WARRANTY_EXPIRATION_DATE]: ({
    data
  }) =>
    getBinauralHearingAidValue(
      data.contextSale,
      PATIENT_SELECTED_INVOICE_OPTIONS.HA_LOSS_DAMAGE_WARRANTY_EXPIRATION_DATE,
      getHearingAidLossDamageWarrantyEnd
    ),
  [PATIENT_SELECTED_INVOICE_OPTIONS.HA_SERVICE_PLAN_EXPIRATION_DATE]: ({
    data
  }) =>
    getBinauralHearingAidValue(
      data.contextSale,
      PATIENT_SELECTED_INVOICE_OPTIONS.HA_SERVICE_PLAN_EXPIRATION_DATE,
      getHearingAidServicePlanExpiration
    ),

  // Receiver merge tags (CS-5372)
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_RECEIVER_MANUFACTURER]: ({ data }) => {
    const item = findAccessoryItem(
      data.contextSale?.items,
      RECEIVER_SUBTYPE,
      "L"
    );
    return item?.product?.master_product?.manufacturer?.name ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_RECEIVER_MODEL]: ({ data }) => {
    const item = findAccessoryItem(
      data.contextSale?.items,
      RECEIVER_SUBTYPE,
      "L"
    );
    return item?.product?.master_product?.model ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_RECEIVER_GAIN]: ({ data }) => {
    const item = findAccessoryItem(
      data.contextSale?.items,
      RECEIVER_SUBTYPE,
      "L"
    );
    if (!item) return "";
    const spec = item.inventory_product?.specification as
      | ReceiverInventoryProductSpecification
      | undefined;
    return spec?.gain ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_RECEIVER_LENGTH]: ({ data }) => {
    const item = findAccessoryItem(
      data.contextSale?.items,
      RECEIVER_SUBTYPE,
      "L"
    );
    if (!item) return "";
    const spec = item.inventory_product?.specification as
      | ReceiverInventoryProductSpecification
      | undefined;
    return spec?.length != null ? String(spec.length) : "";
  },

  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_RECEIVER_MANUFACTURER]: ({
    data
  }) => {
    const item = findAccessoryItem(
      data.contextSale?.items,
      RECEIVER_SUBTYPE,
      "R"
    );
    return item?.product?.master_product?.manufacturer?.name ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_RECEIVER_MODEL]: ({ data }) => {
    const item = findAccessoryItem(
      data.contextSale?.items,
      RECEIVER_SUBTYPE,
      "R"
    );
    return item?.product?.master_product?.model ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_RECEIVER_GAIN]: ({ data }) => {
    const item = findAccessoryItem(
      data.contextSale?.items,
      RECEIVER_SUBTYPE,
      "R"
    );
    if (!item) return "";
    const spec = item.inventory_product?.specification as
      | ReceiverInventoryProductSpecification
      | undefined;
    return spec?.gain ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_RECEIVER_LENGTH]: ({ data }) => {
    const item = findAccessoryItem(
      data.contextSale?.items,
      RECEIVER_SUBTYPE,
      "R"
    );
    if (!item) return "";
    const spec = item.inventory_product?.specification as
      | ReceiverInventoryProductSpecification
      | undefined;
    return spec?.length != null ? String(spec.length) : "";
  },

  // Dome merge tags (CS-5372)
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_DOME_MANUFACTURER]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "L");
    return item?.product?.master_product?.manufacturer?.name ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_DOME_MODEL]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "L");
    return item?.product?.master_product?.model ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_DOME_SIZE]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "L");
    if (!item) return "";
    const spec = item.inventory_product?.specification as
      | DomeInventoryProductSpecification
      | undefined;
    return spec?.size ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_DOME_TYPE]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "L");
    if (!item) return "";
    const spec = item.inventory_product?.specification as
      | DomeInventoryProductSpecification
      | undefined;
    return spec?.type ?? "";
  },

  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_DOME_MANUFACTURER]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "R");
    return item?.product?.master_product?.manufacturer?.name ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_DOME_MODEL]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "R");
    return item?.product?.master_product?.model ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_DOME_SIZE]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "R");
    if (!item) return "";
    const spec = item.inventory_product?.specification as
      | DomeInventoryProductSpecification
      | undefined;
    return spec?.size ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_DOME_TYPE]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "R");
    if (!item) return "";
    const spec = item.inventory_product?.specification as
      | DomeInventoryProductSpecification
      | undefined;
    return spec?.type ?? "";
  }
};
